import { useContext, useEffect, useRef, useState } from 'react'
import { useFormikContext, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { FormContent, FormHeader, FormError, ButtonDrawer, PhoneInput } from '~/components'
import { StepperContext } from '~/components/ui/ViewStepper'
import { StoreContext } from '~/store'
import { wrapWithValidation } from '~/utils'
import { addPhoneNumberSocial } from '~/lib/api'
import { emitCustomEvent } from 'react-custom-events'
import { useTranslation } from 'react-i18next'
import i18next from '~/i18n'

export default function SocialAddPhone({ fullscreen }) {
  const { t } = useTranslation()
  const { prev, next, parentContext } = useContext(StepperContext)
  const { loginAndFetchBookings } = useContext(StoreContext)
  const { values, setFieldValue, setFieldError } = useFormikContext()
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef()

  const handleLoginComplete = (userId) => {
    loginAndFetchBookings(userId)
    if (fullscreen) {
      parentContext?.next('confirm')
    } else {
      emitCustomEvent('SHOW_LOGIN', false)
    }
  }

  const makeRequest = () => {
    setIsLoading(true)
    addPhoneNumberSocial(values.dialCode, values.phone)
      .then(response => {
        const state = response?.deviceActivationState
        if (state === 'PHONE_OTP_INITIATED' || state === 'UPDATE_PHONE_OTP_INITIATED') {
          next('socialValidatePhone')
        } else if (state === 'ACTIVATION_COMPLETE_EXISTING_USER' || state === 'ACTIVATION_COMPLETE_NEW_USER') {
          // Session is fully complete on the server — calling /complete again would fail.
          // Login directly with the userId we already have.
          if (response.userId) {
            handleLoginComplete(response.userId)
          }
        } else if (state === 'EXTERNAL_LOGIN_COMPLETE_EXISTING_USER' || state === 'EXTERNAL_LOGIN_COMPLETE_NEW_USER') {
          // Session needs explicit completion; consent flags and expected actions determine routing.
          if (response.shouldConsentToMarketing !== undefined) {
            setFieldValue('shouldConsentToMarketing', response.shouldConsentToMarketing, false)
          }
          if (response.shouldConsentToTermsAndConditions !== undefined) {
            setFieldValue('shouldConsentToTermsAndConditions', response.shouldConsentToTermsAndConditions, false)
          }
          if (response.userInfo?.firstName) setFieldValue('firstName', response.userInfo.firstName, false)
          if (response.userInfo?.lastName) setFieldValue('lastName', response.userInfo.lastName, false)
          if (response.expectedActions?.includes('ADD_NAME')) {
            next('inputName')
          } else if (response.shouldConsentToTermsAndConditions) {
            next('acceptTerms')
          } else if (response.userId) {
            handleLoginComplete(response.userId)
          }
        }
      })
      .catch(error => {
        setFieldError('phone', error?.response?.message)
      })
      .finally(() => setIsLoading(false))
  }

  const wrappedNext = wrapWithValidation(makeRequest, useFormikContext())

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus()
  }, [inputRef])

  const buttons = [
    { func: prev, isBack: true },
    { func: wrappedNext, title: t('button-continue'), isLoading },
  ]

  return (
    <>
      <FormContent isLoading={isLoading} fullscreen={fullscreen}>
        <FormHeader>{t('header-add-phone-social')}</FormHeader>
        <div className="pb-4 flex flex-col gap-4">
          <PhoneInput innerRef={inputRef} onSubmit={wrappedNext} />
          <ErrorMessage name="phone" component={FormError} />
          <p className="text-body-lg text-on-surface-variant m-0">{t('info-add-phone-social')}</p>
        </div>
      </FormContent>
      <ButtonDrawer buttons={buttons} isLoading={isLoading} fullscreen={fullscreen} />
    </>
  )
}

SocialAddPhone.validationSchema = Yup.object({
  phone: Yup.string()
    .matches(/^\d+$/, i18next.t('validation-only-numbers'))
    .min(6, i18next.t('validation-length-phone'))
    .max(14, i18next.t('validation-length-phone'))
    .required(i18next.t('validation-need-phone')),
})
